import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';

export function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [contentOpacity, setContentOpacity] = useState(0);
  const [isToday, setIsToday] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = rect.height;
      const vh = window.innerHeight;
      // Aparecer: hace falta más scroll (espacio inicial) antes de que el contador se revele
      const enterThreshold = vh * 0.35;
      const appearProgress = Math.max(0, Math.min(1, (enterThreshold - rect.top) / enterThreshold));
      // Desaparecer: al seguir bajando, el contador se va para dar paso a "Nuestra historia"
      const scrolled = -rect.top;
      const disappearProgress = Math.min(1, Math.max(0, scrolled / (sectionHeight * 0.55)));
      setContentOpacity(appearProgress * (1 - disappearProgress));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const weddingDate = new Date('2026-06-27T16:00:00').getTime();

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const difference = weddingDate - now;

      if (difference > 0) {
        setIsToday(false);
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
        });
      } else {
        setIsToday(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const timeUnits = [
    { label: 'Días', value: timeLeft.days, accent: 'var(--emerald)' },
    { label: 'Horas', value: timeLeft.hours, accent: 'var(--chartreuse)' },
    { label: 'Minutos', value: timeLeft.minutes, accent: 'var(--sky-blue)' },
    { label: 'Segundos', value: timeLeft.seconds, accent: 'var(--chartreuse)' },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative pt-[20vh] pb-32 md:pt-[42vh] md:pb-40 px-6 min-h-[120vh] md:min-h-[130vh] flex flex-col justify-center"
      style={{ backgroundColor: '#FFFFF0' }}
    >
      {/* Espacio inicial: al bajar desde el hero se desliza más antes de que aparezca el contador */}
      <div
        className="max-w-4xl mx-auto transition-opacity duration-300 ease-out"
        style={{ opacity: contentOpacity }}
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          {isToday ? (
            <>
              <h2
                className="text-2xl md:text-4xl mb-4 uppercase tracking-[0.2em] leading-tight"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontWeight: 300,
                  color: 'var(--emerald)',
                  letterSpacing: '0.2em',
                }}
              >
                ¡Es hoy! ¡Es hoy! El gran día llegó.
              </h2>
              <p
                className="text-lg md:text-xl max-w-xl mx-auto"
                style={{
                  fontFamily: 'var(--font-serif)',
                  color: 'var(--emerald)',
                  opacity: 0.9,
                }}
              >
                Gracias por ser parte de este momento.
              </p>
              <div className="w-20 h-px mx-auto mt-6" style={{ backgroundColor: 'var(--chartreuse)' }} />
            </>
          ) : (
            <>
              <h2
                className="text-2xl md:text-3xl mb-4 uppercase tracking-[0.3em]"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontWeight: 300,
                  color: 'var(--emerald)',
                  letterSpacing: '0.3em',
                }}
              >
                Faltan...
              </h2>
              <div className="w-20 h-px mx-auto" style={{ backgroundColor: 'var(--chartreuse)' }} />
            </>
          )}
        </motion.div>

        {!isToday && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {timeUnits.map((unit, index) => (
            <motion.div
              key={unit.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="text-center"
            >
              <div
                className="rounded-xl py-8 px-4 border"
                style={{
                  borderColor: unit.accent,
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                }}
              >
                <div
                  className="text-4xl md:text-6xl mb-2"
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontWeight: 300,
                    color: unit.accent,
                  }}
                >
                  {String(unit.value).padStart(2, '0')}
                </div>
                <div
                  className="text-xs md:text-sm uppercase tracking-[0.15em]"
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontWeight: 400,
                    color: 'var(--emerald)',
                    letterSpacing: '0.15em',
                  }}
                >
                  {unit.label}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        )}

        <p
          className="text-center mt-8 uppercase tracking-[0.2em] text-sm md:text-base"
          style={{
            fontFamily: 'var(--font-serif)',
            color: 'var(--emerald)',
            opacity: 0.85,
          }}
        >
          para el gran día
        </p>
      </div>
    </section>
  );
}
